import { fg } from '@confluence/feature-gating';

import type { HydrationError } from './types.ts';

const REACT_ERROR_421_PATTERN = /Minified React error #421/;
const REACT_ERROR_423_PATTERN = /Minified React error #423/;

/**
 * Normalizes a hydration error type by:
 * 1. Stripping the "UNSAFE_" prefix
 * 2. Extracting specific React error codes from ERROR_MESSAGES_FROM_REACT:
 *    - React error #421 → SUSPENSE_UPDATE
 *    - React error #423 → ROOT_RERENDER
 *
 * Returns null if the error has no type.
 */
export const normalizeHydrationErrorType = (error: HydrationError): string | null => {
	if (!error.type) {
		return null;
	}

	const baseType = error.type.startsWith('UNSAFE_') ? error.type.slice(7) : error.type;

	if (
		baseType === 'ERROR_MESSAGES_FROM_REACT' &&
		error.msg &&
		fg('confluence_frontend_sfx_hydration_errors')
	) {
		if (REACT_ERROR_421_PATTERN.test(error.msg)) {
			return 'SUSPENSE_UPDATE';
		}
		if (REACT_ERROR_423_PATTERN.test(error.msg)) {
			return 'ROOT_RERENDER';
		}
	}

	return baseType;
};

/**
 * Returns a count of each hydration error type
 * (e.g. { SERVER_MISMATCH: 3, CLIENT_MISMATCH: 1 }) for lightweight
 * grouping in analytics, independent of the detailed logging gate.
 * Normalizes error types.
 */
export const getHydrationErrorCounts = (
	hydrationErrors: HydrationError[],
): Record<string, number> => {
	const counts: Record<string, number> = {};

	for (const error of hydrationErrors) {
		const normalizedType = normalizeHydrationErrorType(error);
		if (normalizedType) {
			counts[normalizedType] = (counts[normalizedType] || 0) + 1;
		}
	}
	return counts;
};
